import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { languagesNames } from "config";
import { useCvStepNavigation } from "../../../contexts/CvStepNavigationContext";
import { usePatchCvMutation } from "../../../lib/cvsSlice";

const PROFICIENCY_LEVELS = [
  {
    value: "Novice",
    label: "Novice",
    stars: 1,
    description: "Basic awareness. Has theoretical understanding but no or very limited hands-on experience.",
  },
  {
    value: "Beginner",
    label: "Beginner",
    stars: 2,
    description: "Can perform simple tasks with guidance. Limited practical experience. Still learning.",
  },
  {
    value: "Intermediate",
    label: "Intermediate",
    stars: 3,
    description: "Solid understanding. Can work independently in most situations. May need support with complex tasks.",
  },
  {
    value: "Advanced",
    label: "Advanced",
    stars: 4,
    description: "Deep understanding and strong practical experience. Can lead tasks, guide others, and solve complex problems.",
  },
  {
    value: "Expert",
    label: "Expert",
    stars: 5,
    description: "Deep domain expertise combined with leadership influence. Can define best practices, train others, and drive strategy in this area.",
  },
];

const Languages = ({ langs, cvId }) => {
  const [langList, setLangList] = useState(
    Array.isArray(langs) ? langs : []
  );
  const [searchValue, setSearchValue] = useState(null);
  const [inputValue, setInputValue] = useState("");

  // Level picker dialog state
  const [levelDialogOpen, setLevelDialogOpen] = useState(false);
  const [pendingLang, setPendingLang] = useState(null);

  const isDirty = useRef(false);

  const [patchCv] = usePatchCvMutation();
  const { goToNextStep, goToPrevStep } = useCvStepNavigation();

  // Guard against 1-second poll in CvDetails resetting local state
  useEffect(() => {
    if (isDirty.current) return;
    setLangList(Array.isArray(langs) ? langs : []);
  }, [langs, cvId]);

  const addedLanguageNames = langList.map((l) => l.language);

  // Called when user picks a language from autocomplete
  const handleLanguageSelect = (option) => {
    if (!option) return;
    isDirty.current = true;

    if (addedLanguageNames.includes(option.value)) {
      toast.info("Language already added");
      setSearchValue(null);
      setInputValue("");
      return;
    }

    // Open level dialog before saving
    setPendingLang({ language: option.value, proficiency: "Professional" });
    setLevelDialogOpen(true);
    setSearchValue(null);
    setInputValue("");
  };

  const handleLevelSelect = async (level) => {
    if (!pendingLang) return;

    if (pendingLang._id) {
      // Updating proficiency of existing language
      const originalLang = langList.find((l) => l._id === pendingLang._id);
      setLangList((prev) =>
        prev.map((l) =>
          l._id === pendingLang._id ? { ...l, proficiency: level } : l
        )
      );
      setLevelDialogOpen(false);
      setPendingLang(null);

      try {
        await patchCv({
          cvId,
          languages: [{ id: pendingLang._id, proficiency: level }],
        }).unwrap();
      } catch (err) {
        setLangList((prev) =>
          prev.map((l) => (l._id === originalLang._id ? originalLang : l))
        );
        toast.error(
          `Failed to update proficiency: ${err?.data?.message ?? err.message}`
        );
      }
    } else {
      // Adding new language
      setLevelDialogOpen(false);

      try {
        const result = await patchCv({
          cvId,
          languages: [{ language: pendingLang.language, proficiency: level }],
        }).unwrap();

        const newLang = result?.languages?.find(
          (l) =>
            l.language === pendingLang.language &&
            !langList.find((existing) => existing._id === l._id)
        );
        if (newLang) {
          setLangList((prev) => [...prev, newLang]);
        } else {
          setLangList(result?.languages ?? langList);
        }
      } catch (err) {
        toast.error(
          `Failed to add language: ${err?.data?.message ?? err.message}`
        );
      }

      setPendingLang(null);
    }
  };

  const handleOpenLevelDialog = (lang) => {
    isDirty.current = true;
    setPendingLang({ _id: lang._id, language: lang.language, proficiency: lang.proficiency });
    setLevelDialogOpen(true);
  };

  const handleRemove = async (langId) => {
    isDirty.current = true;
    try {
      await patchCv({
        cvId,
        languages: [{ id: langId, delete: true }],
      }).unwrap();
      setLangList((prev) => prev.filter((l) => l._id !== langId));
    } catch (err) {
      toast.error(
        `Failed to remove language: ${err?.data?.message ?? err.message}`
      );
    }
  };

  // NEXT stays disabled while any added language has no proficiency chosen.
  const hasUnsetLevel = langList.some((l) => !l.proficiency);

  const filteredOptions = languagesNames.filter(
    (opt) => !addedLanguageNames.includes(opt.value)
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Search autocomplete */}
      <Autocomplete
        options={filteredOptions}
        getOptionLabel={(opt) => opt.label}
        value={searchValue}
        inputValue={inputValue}
        onInputChange={(_, val) => setInputValue(val)}
        onChange={(_, opt) => handleLanguageSelect(opt)}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search for Language"
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#9E9E9E" }} />
                </InputAdornment>
              ),
            }}
          />
        )}
        noOptionsText="No matching languages found"
      />

      {/* Languages list */}
      {langList.length > 0 && (
        <Box
          sx={{
            border: "1px solid #E0DEDD",
            borderRadius: "8px",
            backgroundColor: "#F4F2F1",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #E0DEDD" }}>
            <Typography
              sx={{
                fontSize: "13px",
                color: "#7A706F",
                fontFamily: "Inter, sans-serif",
              }}
            >
              {langList.length} Language{langList.length !== 1 ? "s" : ""} added
            </Typography>
          </Box>

          {/* Rows */}
          {langList.map((lang) => (
            <Box
              key={lang._id}
              sx={{
                display: "flex",
                alignItems: "center",
                px: 2,
                py: 1.5,
                borderBottom: "1px solid #E0DEDD",
                backgroundColor: "#fff",
                "&:last-child": { borderBottom: "none" },
                gap: 2,
              }}
            >
              <Typography
                sx={{
                  flex: 1,
                  fontSize: "14px",
                  fontFamily: "Inter, sans-serif",
                  color: "#1A1A1A",
                }}
              >
                {lang.language}
              </Typography>

              {/* Proficiency — clickable label that opens level dialog */}
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleOpenLevelDialog(lang)}
                sx={{
                  borderColor: "#E0DEDD",
                  color: "#3D3634",
                  borderRadius: "6px",
                  fontSize: "13px",
                  fontFamily: "Inter, sans-serif",
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  minWidth: 200,
                  justifyContent: "flex-start",
                  "&:hover": {
                    borderColor: "#9E9E9E",
                    backgroundColor: "#F4F2F1",
                  },
                }}
              >
                {lang.proficiency || "Select level"} proficiency
              </Button>

              <IconButton
                size="small"
                onClick={() => handleRemove(lang._id)}
                sx={{ color: "#9E9E9E", "&:hover": { color: "#E84610" } }}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {/* Navigation */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mt: 1,
        }}
      >
        <Button
          variant="outlined"
          onClick={goToPrevStep}
          sx={{
            borderColor: "#C5BFBE",
            color: "#3D3634",
            borderRadius: "6px",
            px: 3,
            fontWeight: 600,
            letterSpacing: "0.5px",
            "&:hover": { borderColor: "#9E9E9E", backgroundColor: "#F4F2F1" },
          }}
        >
          BACK
        </Button>

        <Button
          variant="contained"
          onClick={goToNextStep}
          disabled={hasUnsetLevel}
          sx={{
            backgroundColor: "#E84610",
            "&:hover": { backgroundColor: "#C73A0D" },
            "&:disabled": { backgroundColor: "#E8A090" },
            borderRadius: "6px",
            px: 3,
            fontWeight: 600,
            letterSpacing: "0.5px",
          }}
        >
          NEXT
        </Button>
      </Box>

      {/* Level picker dialog */}
      <Dialog
        open={levelDialogOpen}
        onClose={() => {
          setLevelDialogOpen(false);
          setPendingLang(null);
        }}
        PaperProps={{
          sx: { borderRadius: "12px", minWidth: 360, maxWidth: 420 },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
            typography: "dialogTitle",
            fontSize: "16px",
          }}
        >
          Select Level
          <IconButton
            size="small"
            onClick={() => {
              setLevelDialogOpen(false);
              setPendingLang(null);
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 0, pb: 1, px: 0 }}>
          {PROFICIENCY_LEVELS.map((lvl) => {
            const isSelected = pendingLang?.proficiency === lvl.value;
            return (
              <Box
                key={lvl.value}
                onClick={() => handleLevelSelect(lvl.value)}
                sx={{
                  px: 3,
                  py: 1.5,
                  backgroundColor: isSelected ? "background.neutral" : "transparent",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "background.neutral" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
                    {lvl.label}
                  </Typography>
                  <Box sx={{ display: "flex" }}>
                    {Array.from({ length: 5 }).map((_, i) =>
                      i < lvl.stars
                        ? <StarIcon key={i} sx={{ fontSize: 14, color: "text.primary" }} />
                        : <StarBorderIcon key={i} sx={{ fontSize: 14, color: "text.primary" }} />
                    )}
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ color: "text.muted" }}>
                  {lvl.description}
                </Typography>
              </Box>
            );
          })}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Languages;
